import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { ExternalLink, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { lookupBlockchain, type BlockchainResult } from "@/lib/api";
import { IMG_BASE, POPULAR_CHAINS } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

interface BlockchainPageProps {
  serverData?: BlockchainResult | null;
}

export default function BlockchainPage({ serverData }: BlockchainPageProps) {
  const { id } = useParams<{ id: string }>();
  const [chain, setChain] = useState<BlockchainResult | null>(serverData ?? null);
  const [loading, setLoading] = useState(!serverData);

  useEffect(() => {
    if (serverData) return;
    if (!id) return;
    setLoading(true);
    setChain(null);
    lookupBlockchain(id)
      .then(setChain)
      .catch(() => setChain({ error: "Failed to load chain data" } as BlockchainResult))
      .finally(() => setLoading(false));
  }, [id, serverData]);

  if (loading) {
    return (
      <div className="py-32 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Loading chain data...</p>
      </div>
    );
  }

  if (!chain || chain.error) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <AlertCircle className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Blockchain not found</h1>
          <p className="text-sm text-secondary-foreground mb-6">
            {chain?.error || `We couldn't find chain "${id}".`}
          </p>
          <Button asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4" />Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout breadcrumbs={[{ label: "Blockchains", to: "/" }, { label: chain.name }]}>
      {/* Hero */}
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0">
            <img
              src={`${IMG_BASE}/blockchain/${chain.chainId}?size=256`}
              alt={`${chain.name} logo`}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-contain bg-white border shadow-sm"
              onError={(e) => { (e.target as HTMLElement).style.opacity = "0.3"; }}
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">{chain.name}</h1>
            <div className="flex gap-2 flex-wrap mb-3">
              <Badge variant="secondary">Chain ID: {chain.chainId}</Badge>
              {chain.shortName && <Badge variant="outline">{chain.shortName}</Badge>}
              {chain.testnet && <Badge variant="outline" className="text-amber-600">Testnet</Badge>}
            </div>
            {chain.nativeCurrency && (
              <p className="text-sm text-secondary-foreground">
                Native Currency: <span className="font-semibold">{chain.nativeCurrency.name} ({chain.nativeCurrency.symbol})</span>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Quick Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/blockchain/${chain.chainId}" />`} />
      </section>

      {/* Block Explorers */}
      {chain.explorers?.length > 0 && (
        <section className="mb-12">
          <h2 className="text-lg font-bold tracking-tight mb-3">Block Explorers</h2>
          <div className="flex gap-2 flex-wrap">
            {chain.explorers.map((explorer) => (
              <a key={explorer.url} href={explorer.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-card border rounded-xl text-sm font-medium text-secondary-foreground hover:text-foreground hover:border-border-strong hover:shadow-sm transition-all no-underline">
                {explorer.name} <ExternalLink className="w-3 h-3 text-muted-foreground" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* API Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- Chain logo image -->\n<img src="${IMG_BASE}/blockchain/${chain.chainId}" />\n\n<!-- With size parameter -->\n<img src="${IMG_BASE}/blockchain/${chain.chainId}?size=256" />\n\n<!-- Chain metadata (JSON) -->\n<script>\nfetch("https://api.loadlogo.com/blockchain/${chain.chainId}")\n</script>`} />
      </section>

      {/* Related Chains */}
      <section>
        <h2 className="text-lg font-bold tracking-tight mb-4">Popular Blockchains</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {POPULAR_CHAINS.filter((c) => c.id !== String(chain.chainId)).slice(0, 8).map((c) => (
            <Link key={c.id} to={`/blockchain/${c.id}`} className="flex items-center gap-3 p-3 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all no-underline">
              <img src={`${IMG_BASE}/blockchain/${c.id}?size=64`} alt={c.name} className="w-8 h-8 rounded-lg object-contain" loading="lazy" />
              <div className="min-w-0">
                <span className="text-sm font-medium text-secondary-foreground block truncate">{c.name}</span>
                <span className="text-xs text-muted-foreground">ID: {c.id}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
